import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';

import { CreateFieldInterventionService } from '@connect-services/client/services/create-field-intervention.service';
import { ModeUiSource } from '@connect-services/client/services/edit-mode-layer-panel.service';
import { GeographicFilterService } from '@connect-services/client/services/geographic-filter.service';
import { FilterModeEnum, SelectionService } from '@connect-services/client/services/map/selection/selection.service';
import { MapDrawService } from '@connect-services/client/services/map-draw.service';
import { MapSourceService } from '@connect-services/client/services/map-source.service';
import { SelectZoneLayerService } from '@connect-services/client/services/select-zone-layer.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { RessourcesModule } from '@ressources/client/ressources.module';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, RessourcesModule, SelectComponent],
    selector: 'app-map-modal-filter',
    templateUrl: './map-modal-filter.component.html',
})
export class MapModalFilterComponent {
    protected readonly ButtonType = ButtonType;
    protected readonly FilterModeEnum = FilterModeEnum;
    protected readonly mapDrawService = inject(MapDrawService);
    protected readonly ModeUiSource = ModeUiSource;
    protected readonly selectionService = inject(SelectionService);

    private readonly createFieldInterventionService = inject(CreateFieldInterventionService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly geographicFilterService = inject(GeographicFilterService);
    private readonly mapSourceService = inject(MapSourceService);
    private readonly selectZoneLayerService = inject(SelectZoneLayerService);

    public constructor() {
        this.mapDrawService.initializeDrawEngine();

        this.destroyRef.onDestroy(async () => {
            await this.selectionService.clearAllSelections();
        });
    }

    protected async onLayerGroupChange(selectedOptions: SelectOption<string[]>[]): Promise<void> {
        const first: SelectOption<string[]> = selectedOptions[0];

        if (!first) {
            this.selectionService.selectedLayerIds.set([]);
            await this.selectZoneLayerService.removeLayers();

            return;
        }

        this.selectionService.selectedLayerIds.set(first.value);
        await this.selectZoneLayerService.addSelectedLayerEntities(first.value);

        if (this.selectionService.isFieldInterventionSelectionEnabled()) {
            await this.mapSourceService.drawSources(this.createFieldInterventionService.currentSources(), {
                fitBounds: false,
                zoneDelemiter: this.createFieldInterventionService.getFieldInterventionAreaZoneDelimiter(),
            });
        }
    }

    protected async toggleSelectLayer(): Promise<void> {
        if (this.selectionService.selectionMode() === FilterModeEnum.SELECT_ZONE) {
            this.selectionService.selectedLayerIds.set([]);
            await this.selectZoneLayerService.removeLayers();

            this.selectionService.selectionMode.set(null);
            this.geographicFilterService.isSelectZoneActive.set(false);

            return;
        }

        this.selectionService.selectionMode.set(FilterModeEnum.SELECT_ZONE);
        this.geographicFilterService.isSelectZoneActive.set(true);
        this.mapDrawService.clearDrawnPolygons();
    }
}
